import React, { useCallback } from "react";
import {
  categorySelector,
  isCategoryMapSelector,
  repTop30Selector,
} from "@app-redux/selectors";
import { useAppDispatch, useAppSelector } from "@app-redux/store";
import { getRepTop30 } from "@app-redux/thunk/repTop30Thunk";
import { RelationshipLevelSelect } from "@components/RelationshipLevelSelect";
import { CountryFlag } from "@components/CountryFlag";
import { Loader } from "@components/Loader";
import { ROUTER_PATHS } from "@constants/index";
import Avatar from "@mui/material/Avatar";
import { getCategoryItem, getSortedCategories, stringAvatar } from "@utils/index";
import { Link } from "react-router-dom";
import styles from "./styles.module.css";

const getSubtitleText = (repName: string, count: number): string => {
  if (count >= 30) return "";
  if (count === 0)
    return `${repName} does not have any Relationship Levels assigned yet`;
  if (count === 1)
    return `${repName} has 1 contact with a Relationship Level assigned`;
  return `${repName} has ${count} contacts with a Relationship Level assigned`;
};

export const RepTop30 = () => {
  const dispatch = useAppDispatch();
  const { data: items, isLoading, selectedRep, error } =
    useAppSelector(repTop30Selector);
  const { data: categoryList, map } = useAppSelector(categorySelector);
  const isCategoryMap = useAppSelector(isCategoryMapSelector);

  const handleLevelChanged = useCallback(() => {
    if (selectedRep) {
      dispatch(getRepTop30({ repId: selectedRep.id, silent: true }));
    }
  }, [dispatch, selectedRep]);

  const getCategoryNames = (arr: string[]) =>
    arr
      .map((id) => getCategoryItem(id, categoryList, map)?.name)
      .filter(Boolean)
      .join(", ");

  const getCountryNames = (arr: string[]) =>
    arr.map((id) => {
      const item = getCategoryItem(id, categoryList, map);
      return item ? item.name : "";
    });

  if (!selectedRep) {
    return (
      <div className={styles.container}>
        <div className={styles.topPanel}>
          <h1>Rep Top 30</h1>
        </div>
        <p className={styles.emptyMessage}>Select a rep to view their Top 30.</p>
      </div>
    );
  }

  const subtitle = !isLoading ? getSubtitleText(selectedRep.name, items.length) : "";
  const totalScore = items.reduce(
    (sum, item) => sum + (item.relationship_level ?? 0),
    0
  );

  return (
    <div className={styles.container}>
      <div className={styles.topPanel}>
        <h1>{selectedRep.name}'s Top 30</h1>
        {!isLoading && items.length > 0 && (
          <span className={styles.totalScore}>Total Score: {totalScore}</span>
        )}
        {subtitle && (
          <span className={styles.resultsLabel}>{subtitle}</span>
        )}
      </div>
      <div className={styles.tableWrapper}>
        <Loader className="profileList" show={isLoading} skipWait />
        {error && <p className={styles.errorMessage}>{error}</p>}
        {!isLoading && items.length === 0 && !error && (
          <p className={styles.emptyMessage}>
            No Relationship Levels assigned
          </p>
        )}
        {items.length > 0 && (
          <table aria-label="Rep Top 30 table" className={styles.table}>
            <colgroup>
              <col width="4%" />
              <col width="22%" />
              <col width="15%" />
              <col width="15%" />
              <col width="15%" />
              <col width="18%" />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.rankHeader}>#</th>
                <th>Person</th>
                <th>Relationship Level</th>
                <th>Role</th>
                <th>Market</th>
                <th>Country</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const sortedRoles = isCategoryMap
                  ? getSortedCategories(item.roles_ids ?? [], map, isCategoryMap)
                  : item.roles_ids ?? [];
                const sortedMarkets = isCategoryMap
                  ? getSortedCategories(item.markets_ids ?? [], map, isCategoryMap)
                  : item.markets_ids ?? [];
                const sortedLocations = isCategoryMap
                  ? getSortedCategories(item.locations_ids ?? [], map, isCategoryMap)
                  : item.locations_ids ?? [];

                return (
                  <tr key={item.id}>
                    <td className={styles.rankCell}>{index + 1}</td>
                    <td>
                      <div className={styles.profileName}>
                        <Avatar
                          alt={item.name}
                          src={item.image_url}
                          {...stringAvatar(item.name)}
                        />
                        <Link to={`/${ROUTER_PATHS.profile}/${item.id}`}>
                          <span>{item.name}</span>
                        </Link>
                      </div>
                    </td>
                    <td>
                      <RelationshipLevelSelect
                        profileId={item.id}
                        currentLevel={item.relationship_level}
                        onLevelChanged={handleLevelChanged}
                      />
                    </td>
                    <td>{getCategoryNames(sortedRoles)}</td>
                    <td>{getCategoryNames(sortedMarkets)}</td>
                    <td>
                      {getCountryNames(sortedLocations).map((c) => (
                        <div key={c} className={styles.countryBlock}>
                          <CountryFlag country={c} />
                          <p>{c}</p>
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
